import React, { useEffect, useState } from "react";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { auth } from "@/lib/firebase";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, verifyBeforeUpdateEmail } from "firebase/auth";

export default function SettingsPage() {
    const { user, refresh } = useAuth();

    const [profile, setProfile] = useState({ name: "", email: "" });
    const [savingProfile, setSavingProfile] = useState(false);

    const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
    const [savingPassword, setSavingPassword] = useState(false);

    const load = async () => {};

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        setProfile({ name: user?.name || "", email: user?.email || "" });
    }, [user?.name, user?.email]);

    const saveProfile = async (e) => {
        e.preventDefault();
        const nextName = (profile.name || "").trim();
        const nextEmail = (profile.email || "").trim().toLowerCase();

        if (!nextName) {
            toast.error("Name is required");
            return;
        }

        setSavingProfile(true);
        try {
            const emailChanged = !!nextEmail && nextEmail !== (user?.email || "").toLowerCase();

            if (emailChanged && auth.currentUser) {
                await verifyBeforeUpdateEmail(auth.currentUser, nextEmail);
                await api.patch("/settings/profile", { name: nextName });
                toast.success("Name updated. Email verification link sent to your new address.");
            } else {
                const payload = { name: nextName };
                if (emailChanged) payload.email = nextEmail;
                await api.patch("/settings/profile", payload);
                if (emailChanged && !auth.currentUser) {
                    toast.success("Profile updated. Firebase session not found, email changed directly in dashboard.");
                } else {
                    toast.success("Profile updated");
                }
            }

            await refresh();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setSavingProfile(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (!passwordForm.current_password || !passwordForm.new_password) {
            toast.error("Current and new password are required");
            return;
        }
        setSavingPassword(true);
        try {
            await api.post("/settings/change-password", passwordForm);

            if (auth.currentUser?.email) {
                const cred = EmailAuthProvider.credential(auth.currentUser.email, passwordForm.current_password);
                await reauthenticateWithCredential(auth.currentUser, cred);
                await updatePassword(auth.currentUser, passwordForm.new_password);
            }

            setPasswordForm({ current_password: "", new_password: "" });
            toast.success("Password updated");
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setSavingPassword(false);
        }
    };

    return (
        <div data-testid="settings-page">
            <PageHeader eyebrow="Settings" title="Profile and security." />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <form onSubmit={saveProfile} className="lg:col-span-1 bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                    <h3 className="font-display text-[22px] font-semibold tracking-tight mb-5">Profile</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Name</label>
                            <input
                                value={profile.name}
                                onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                                className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                                data-testid="settings-name"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Email</label>
                            <input
                                value={profile.email}
                                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                                className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px]"
                            />
                            <p className="mt-1 text-[11px] text-ink-muted">Changing email sends a Firebase verification link when Firebase session is available.</p>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Role</label>
                            <input defaultValue={user?.role || "user"} disabled className="w-full bg-cream-200 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] text-ink-muted capitalize" />
                        </div>
                        <button disabled={savingProfile} className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-3 text-[14px] font-semibold transition-colors disabled:opacity-70">
                            {savingProfile ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </form>

                <div className="lg:col-span-2 space-y-5">
                    <form onSubmit={changePassword} className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <h3 className="font-display text-[22px] font-semibold tracking-tight mb-1">Security</h3>
                        <p className="text-[12.5px] text-ink-muted mb-4">Update your dashboard password. If you are signed into Firebase, password changes are synced there too.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                                type="password"
                                value={passwordForm.current_password}
                                onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))}
                                placeholder="Current password"
                                className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px]"
                            />
                            <input
                                type="password"
                                value={passwordForm.new_password}
                                onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))}
                                placeholder="New password"
                                className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px]"
                            />
                        </div>
                        <button disabled={savingPassword} className="mt-3 bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-2.5 text-[13px] font-semibold disabled:opacity-70">
                            {savingPassword ? "Updating..." : "Change password"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
